/**
 * Sauna Kit Builder — self-initialising custom element.
 *
 * Previously this logic lived in an inline <script type="module"> inside
 * blocks/sauna-kit-builder.liquid. That inline script never executed when the
 * quick-add modal injected a cloned copy of the product page (scripts inserted
 * via innerHTML/morph don't run), so the builder was inert in the overlay.
 *
 * By registering it as a custom element and loading this module globally, every
 * <sauna-kit-builder> initialises via connectedCallback — on the product page
 * AND when morphed into the quick-add modal. Listeners are delegated to the root
 * element and all state is read fresh from the DOM, so it survives the modal's
 * morph-based re-rendering.
 *
 * Mirrors product-form.js #processBatchAddToCart: a single /cart/add.js with an
 * items array + the theme's native CartLinesUpdateEvent so the cart drawer opens.
 *
 * Variant changes: the theme's variant picker only morphs itself; every other
 * component syncs from the ProductSelectEvent's fetched HTML (see
 * product-price.js). The builder does the same so auto-computed tier prices
 * track the selected variant (e.g. 600ml vs 1L bottle).
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Market-aware money formatting: reuse the format of a server-rendered price
 * inside the builder (which Liquid's money filter already localised), swapping
 * in the new amount. Falls back to Intl with the active Shopify currency, then
 * to a plain "$" prefix.
 */
const money = (n, scope) => {
  const sample = scope
    ?.querySelector('.kit-tier__price, .kit-tier__compare, .kit-acc__price')
    ?.textContent.trim();
  if (sample && /\d/.test(sample)) {
    // Follow the sample's separators: "€30,95" (comma decimal) vs "$1,234.00".
    const commaDecimal = /\d,\d{2}(?!\d)/.test(sample);
    const hasDecimals = /\d[.,]\d{2}(?!\d)/.test(sample);
    let [int, dec] = (Math.round(n * 100) / 100).toFixed(2).split('.');
    int = int.replace(/\B(?=(\d{3})+(?!\d))/g, commaDecimal ? '.' : ',');
    const amount = !hasDecimals && dec === '00' ? int : int + (commaDecimal ? ',' : '.') + dec;
    return sample.replace(/\d(?:[\d.,]*\d)?/, amount);
  }
  const code = window.Shopify?.currency?.active;
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(n);
    } catch {
      /* unknown code — fall through */
    }
  }
  return '$' + (Math.round(n * 100) / 100).toFixed(2);
};
const num = (v) => {
  const f = parseFloat(String(v || '').replace(/[^0-9.]/g, ''));
  return isNaN(f) ? 0 : f;
};

class SaunaKitBuilder extends HTMLElement {
  connectedCallback() {
    this.#sectionEl = this.closest('.shopify-section, dialog');
    this.#sectionEl?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    // Re-render on reconnect/morph, but only wire instance listeners once.
    if (this.__kitInit) {
      this.#initStepper();
      this.#render();
      this.#initWalletButton();
      return;
    }
    this.__kitInit = true;

    this.addEventListener('change', (event) => {
      if (event.target.closest('.kit-tier__input, .kit-acc__input')) this.#render();
    });

    this.addEventListener('click', (event) => {
      const stepBtn = event.target.closest('.kit-qty__btn');
      if (stepBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.#step(stepBtn);
        return;
      }
      const addBtn = event.target.closest('[data-kit-add]');
      if (addBtn) {
        event.preventDefault();
        this.#add(addBtn);
        return;
      }
      const walletIntercept = event.target.closest('[data-kit-wallet-intercept]');
      if (walletIntercept) {
        event.preventDefault();
        event.stopPropagation();
        this.#expressCheckout(walletIntercept);
      }
    });

    // When the quick-add modal morphs fresh content in, keep the summary correct.
    this.#observer = new MutationObserver(() => {
      cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => this.#render());
    });
    this.#observer.observe(this, { childList: true, subtree: true });

    this.#initStepper();
    this.#render();
    this.#initWalletButton();
  }

  /**
   * Shopify auto-initialises payment buttons present at page load; buttons
   * injected later (quick-add modal morph) need an explicit init. Safe no-op
   * when the SDK hasn't loaded or the button is already initialised.
   */
  #initWalletButton() {
    if (this.querySelector('.kit-wallet') && window.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#sectionEl?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#sectionEl = null;
  }

  /** @type {MutationObserver | undefined} */
  #observer;
  /** @type {number | undefined} */
  #raf;
  /** @type {Element | null} */
  #sectionEl = null;

  /**
   * Variant changed: the picker's fetched page HTML contains this block
   * re-rendered for the new variant — sync tier prices from it.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        const fresh = detail.html.querySelector(`sauna-kit-builder[data-block-id="${this.dataset.blockId}"]`);
        if (fresh) this.#syncFrom(fresh);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sauna-kit] product select:', error);
      });
  };

  /**
   * Pulls variant-dependent prices out of a freshly rendered copy of this
   * block, preserving the user's tier selection and stepper quantity.
   * @param {Element} fresh
   */
  #syncFrom(fresh) {
    if (fresh instanceof HTMLElement && fresh.dataset.defaultVariant) {
      this.dataset.defaultVariant = fresh.dataset.defaultVariant;
    }

    const freshByValue = new Map();
    fresh.querySelectorAll('.kit-tier__input').forEach((inp) => freshByValue.set(inp.value, inp));

    this.querySelectorAll('.kit-tier__input').forEach((inp) => {
      const src = freshByValue.get(inp.value);
      if (!src) return;
      const row = inp.closest('.kit-tier');
      const srcRow = src.closest('.kit-tier');

      inp.dataset.compare = src.dataset.compare || '';
      const cmpEl = row?.querySelector('.kit-tier__compare');
      const srcCmpEl = srcRow?.querySelector('.kit-tier__compare');
      if (cmpEl && srcCmpEl) this.#setText(cmpEl, srcCmpEl.textContent.trim());

      if (row?.classList.contains('kit-tier--stepper')) {
        // Stepper tier: swap the unit price, keep the chosen quantity.
        inp.dataset.unitPrice = src.dataset.unitPrice || src.dataset.price;
        this.#applyQty(row);
      } else {
        inp.dataset.price = src.dataset.price;
        const priceEl = row?.querySelector('.kit-tier__price');
        const srcPriceEl = srcRow?.querySelector('.kit-tier__price');
        if (priceEl && srcPriceEl) this.#setText(priceEl, srcPriceEl.textContent.trim());
      }
    });

    this.#render();
  }

  #selectedTier() {
    return this.querySelector('.kit-tier__input:checked');
  }

  #initStepper() {
    const stepRow = this.querySelector('.kit-tier--stepper');
    if (!stepRow) return;
    const qtyInput = stepRow.querySelector('.kit-tier__input');
    if (!qtyInput) return;
    if (!qtyInput.dataset.unitPrice) qtyInput.dataset.unitPrice = qtyInput.dataset.price;
    this.#applyQty(stepRow);
  }

  #applyQty(stepRow) {
    const qtyInput = stepRow.querySelector('.kit-tier__input');
    const valEl = stepRow.querySelector('[data-kit-qty-val]');
    const titleEl = stepRow.querySelector('.kit-tier__title');
    const priceEl = stepRow.querySelector('.kit-tier__price');
    if (!qtyInput) return;
    if (!stepRow.dataset.origTitle && titleEl) stepRow.dataset.origTitle = titleEl.textContent.trim();

    const unit = num(qtyInput.dataset.unitPrice) || num(qtyInput.dataset.price);
    const q = Math.max(1, Math.min(12, parseInt(qtyInput.dataset.qty, 10) || 1));
    qtyInput.dataset.qty = String(q);
    qtyInput.dataset.price = String(unit * q);

    if (valEl) this.#setText(valEl, String(q));
    const noun = this.dataset.noun || 'hat';
    const pluralTitle = noun.charAt(0).toUpperCase() + noun.slice(1) + 's';
    if (titleEl) this.#setText(titleEl, q === 1 ? stepRow.dataset.origTitle || titleEl.textContent : q + ' ' + pluralTitle);
    if (priceEl) this.#setText(priceEl, money(unit * q, this));
  }

  #step(btn) {
    const stepRow = btn.closest('.kit-tier--stepper');
    if (!stepRow) return;
    const qtyInput = stepRow.querySelector('.kit-tier__input');
    if (!qtyInput) return;
    let q = parseInt(qtyInput.dataset.qty, 10) || 1;
    q += parseInt(btn.dataset.step, 10) || 0;
    qtyInput.dataset.qty = String(Math.max(1, Math.min(12, q)));
    qtyInput.checked = true; // selecting multiple single hats selects this tier
    this.#applyQty(stepRow);
    this.#render();
  }

  #render() {
    const tier = this.#selectedTier();
    const hatQty = tier ? parseInt(tier.dataset.qty, 10) || 1 : 1;
    let total = tier ? num(tier.dataset.price) : 0;
    let compare = tier ? num(tier.dataset.compare) || num(tier.dataset.price) : 0;
    let accCount = 0;

    this.querySelectorAll('.kit-acc__input').forEach((a) => {
      if (a.checked) {
        accCount++;
        total += num(a.dataset.price);
        compare += num(a.dataset.compare) || num(a.dataset.price);
      }
    });

    const noun = this.dataset.noun || 'hat';
    const parts = [hatQty + ' ' + (hatQty === 1 ? noun : noun + 's')];
    if (accCount > 0) parts.push('+ ' + accCount + (accCount === 1 ? ' add-on' : ' add-ons'));

    const summaryText = this.querySelector('[data-kit-summary-text]');
    const totalEl = this.querySelector('[data-kit-total]');
    const saveEl = this.querySelector('[data-kit-save]');
    if (summaryText) this.#setText(summaryText, 'Your kit: ' + parts.join(' '));
    if (totalEl) this.#setText(totalEl, money(total, this));
    const saved = compare - total;
    if (saveEl) this.#setText(saveEl, saved > 0.005 ? 'You save ' + money(saved, this) : '');

    // Mirror the kit's hat qty + selected variant into the wallet express-checkout form.
    const walletQty = this.querySelector('[data-kit-wallet-qty]');
    if (walletQty) walletQty.value = String(hatQty);
    const walletVariant = this.querySelector('[data-kit-wallet-variant]');
    if (walletVariant) {
      const variantId = this.#currentHatVariant();
      if (variantId) walletVariant.value = String(variantId);
    }

    // The wallet form can only carry the hats — while add-ons are ticked, an
    // invisible overlay intercepts the wallet tap and routes the full kit
    // through the cart to /checkout instead (see #expressCheckout).
    const walletIntercept = this.querySelector('[data-kit-wallet-intercept]');
    if (walletIntercept) walletIntercept.hidden = accCount === 0;
  }

  /** Sets textContent while pausing the MutationObserver to avoid a feedback loop. */
  #setText(el, text) {
    if (el.textContent === text) return;
    this.#observer?.disconnect();
    el.textContent = text;
    if (this.isConnected) this.#observer?.observe(this, { childList: true, subtree: true });
  }

  /**
   * Resolves the currently-selected hat variant. Works on the product page and
   * inside the quick-add modal by scoping to the enclosing <product-component>,
   * so it never reads another card's form.
   */
  #currentHatVariant() {
    const scope = this.closest('product-component') || document;
    const radio = scope.querySelector('input[type="radio"][data-variant-id]:checked');
    if (radio && radio.dataset.variantId) return radio.dataset.variantId;

    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    if (fromUrl) return fromUrl;

    const input = scope.querySelector('product-form-component input[name="id"]');
    if (input && input.value) return input.value;

    return this.dataset.defaultVariant;
  }

  /** The kit as a /cart/add.js items array (selected hat × tier qty + checked add-ons). */
  #kitItems() {
    const tier = this.#selectedTier();
    const hatQty = tier ? parseInt(tier.dataset.qty, 10) || 1 : 1;
    const hatVariantId = this.#currentHatVariant();
    if (!hatVariantId) return null;

    const items = [{ id: Number(hatVariantId), quantity: hatQty }];
    this.querySelectorAll('.kit-acc__input').forEach((a) => {
      if (a.checked && a.dataset.variantId) items.push({ id: Number(a.dataset.variantId), quantity: 1 });
    });
    return items;
  }

  /**
   * Wallet tap while add-ons are ticked: a product-form express checkout can't
   * carry the add-ons, so add the whole kit to the cart and go straight to
   * /checkout — the same wallet buttons (Apple/Google/Shop Pay) sit at the top
   * of Shopify's mobile checkout, now with the add-ons included.
   */
  async #expressCheckout(overlay) {
    if (this.__expressBusy) return;
    const items = this.#kitItems();
    if (!items) return;
    // Same attribution the wallet form carries, so the pdp-express Flow tag still fires.
    items[0].properties = { ...items[0].properties, _checkout_source: 'PDP express wallet' };

    this.__expressBusy = true;
    overlay.closest('.kit-wallet')?.classList.add('kit-wallet--busy');

    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const response = await res.json();
      if (response.status) {
        this.#expressError(overlay, response.message || 'Unavailable');
        return;
      }
      window.location.assign('/checkout');
    } catch {
      this.#expressError(overlay, 'Error — try again');
    }
  }

  /** Surfaces an express-checkout failure on the Add Bundle button, then resets. */
  #expressError(overlay, message) {
    this.__expressBusy = false;
    overlay.closest('.kit-wallet')?.classList.remove('kit-wallet--busy');
    const btn = this.querySelector('[data-kit-add]');
    if (!btn) return;
    const origLabel = btn.dataset.label || btn.textContent.trim();
    this.#setText(btn, message);
    setTimeout(() => this.#setText(btn, origLabel), 2500);
  }

  async #add(btn) {
    const items = this.#kitItems();
    if (!items) return;

    const origLabel = btn.dataset.label || btn.textContent.trim();
    btn.disabled = true;
    btn.textContent = 'Adding…';

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const totalQuantity = items.reduce((s, it) => s + it.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();
    const target = this.closest('product-form-component') || document.querySelector('product-form-component') || document;
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
        promise: deferred.promise,
      })
    );

    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const response = await res.json();

      if (response.status) {
        target.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        const ajaxCart = await fetch('/cart.js').then((r) => r.json());
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'sauna-kit-builder', sourceId: this.id, itemCount: totalQuantity },
        });
        btn.textContent = response.message || 'Unavailable';
        setTimeout(() => {
          btn.textContent = origLabel;
          btn.disabled = false;
        }, 2500);
        return;
      }

      const cart = await fetch('/cart.js').then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sauna-kit-builder',
          sourceId: this.id,
          itemCount: totalQuantity,
          sections: response.sections,
          didError: false,
        },
      });

      btn.textContent = '✓ Added!';
      setTimeout(() => {
        btn.textContent = origLabel;
        btn.disabled = false;
      }, 2000);
    } catch (err) {
      deferred.reject(err);
      btn.textContent = 'Error — Try Again';
      setTimeout(() => {
        btn.textContent = origLabel;
        btn.disabled = false;
      }, 2500);
    }
  }
}

if (!customElements.get('sauna-kit-builder')) {
  customElements.define('sauna-kit-builder', SaunaKitBuilder);
}

